/**
 * Lehrkräfte ohne Mitarbeitervertrag: batched, cached contract status (contract-manager GET employee-contracts/status) for the
 * hint when a teacher is assigned (course dialogs, pickers, substitutions) and on the teacher profile. Only a warning, never
 * blocking. Unknown status (no right, older backend, error) = no hint.
 */
import {CARD_LOAD_OPTIONS} from '@/classes/api/api.class';
import {ContractManagerApi, EmployeeContractStatus} from '@/classes/api/contract-manager.api.class';
import {PayoffManagerExceptions} from '@/exceptions/payoff-manager';
import {contractKeyOf, createContractStatusLoader, MISSING_CONTRACT_CODE, MISSING_CONTRACT_HINT, MISSING_CONTRACT_TAG} from './teacherContractStatus.loader';

export {contractKeyOf, createContractStatusLoader, MISSING_CONTRACT_CODE, MISSING_CONTRACT_HINT, MISSING_CONTRACT_TAG};

export const teacherContracts = createContractStatusLoader(async (ids, withActiveCourses) => {
    const resp = await ContractManagerApi.employeeContracts.status({employees: ids, ...(withActiveCourses ? {withActiveCourses} : {})}, CARD_LOAD_OPTIONS);
    return resp?.statuses || null;
});

/** Comma separated ids (stable watch key, see contractKeyOf) → { id: true } for teachers known to have no contract */
export async function missingContractMap(keys: string): Promise<Record<string, boolean>> {
    const ids = keys ? keys.split(',') : [];
    const statuses = await teacherContracts.load(ids).catch(() => new Map<string, EmployeeContractStatus>());
    return Object.fromEntries(ids.map((id) => [id, statuses.get(id)?.hasContract === false]));
}

/** Status of one teacher: true = no contract (known), false = contract or unknown */
export async function lacksContract(id: unknown): Promise<boolean> {
    if (!id) {
        return false;
    }
    const status = (await teacherContracts.load([id])).get(String(id));
    return !!status && !status.hasContract;
}

/** MissingContractException of the payoff-manager (by type or, newer backends, by code) */
export function isMissingContractError(e: unknown): boolean {
    return e instanceof PayoffManagerExceptions.MissingContractException || (e as { code?: string } | undefined)?.code === MISSING_CONTRACT_CODE;
}
